import { Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { DatabaseFieldType, type Hyperlink } from '@databases/types/database';
import { MultiValueChangeType } from '@databases/utils/database/changesets';
import React, { type FunctionComponent, useCallback } from 'react';

import { FieldValueRemoveButton, filterOutRemovedValue } from '../popover-edit-common';
import { type EditComponentProps } from '../types';

const hyperlinkPopoverValueRowStyles = xcss({
  maxWidth: '100%',
  minWidth: '0'
});

interface CurrentDataSectionValueProps extends EditComponentProps<DatabaseFieldType.HYPERLINK> {
  link: Hyperlink & {
    _id: string;
  };
  setHasCurrentData: React.Dispatch<React.SetStateAction<boolean>>;
  isMulti: boolean;
}

export const CurrentDataSectionValue: FunctionComponent<CurrentDataSectionValueProps> = ({
  onChange,
  onBlur,
  autoFocus,
  value: linkValues,
  link,
  setHasCurrentData,
  isMulti
}) => {
  const handleCurrentDataSectionBlur = useCallback(() => {
    setHasCurrentData(
      linkValues.length > 0 && linkValues.some((link) => link.url !== '')
    );
    onBlur?.();
  }, [linkValues, onBlur, setHasCurrentData]);

  const linkValueIndex = linkValues.findIndex(
    (value) => link._id !== '' && value._id === link._id
  );
  const remainingValues = filterOutRemovedValue({
    currentValues: linkValues,
    indexToRemove: linkValueIndex
  });

  const onItemDelete = useCallback(() => {
    onChange(remainingValues, {
      type: DatabaseFieldType.HYPERLINK,
      delta: {
        type: MultiValueChangeType.REMOVE,
        index: linkValueIndex
      }
    });

    if (isMulti) {
      setHasCurrentData(
        linkValues.length > 0 && linkValues.some((link) => link.url !== '')
      );
    } else {
      setHasCurrentData(false);
    }
  }, [onChange, linkValues, linkValueIndex, isMulti, setHasCurrentData, remainingValues]);

  return (
    <Flex key={link._id} alignItems={'center'} xcss={hyperlinkPopoverValueRowStyles}>
      <div
        data-testid="hyperlink-popover-value"
        style={{
          border: `${token('border.width')} solid ${token('color.border')}`,
          borderRadius: token('radius.small', '3px'),
          boxSizing: 'border-box',
          flex: '0 1 auto',
          minWidth: 0,
          maxWidth: `calc(100% - ${token('space.300', '24px')})`,
          paddingLeft: token('space.050', '4px'),
          paddingRight: token('space.050', '4px'),
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden'
        }}
      >
        {link.url}
      </div>
      <FieldValueRemoveButton
        testId="hyperlink-popover-value-remove"
        onItemBlur={handleCurrentDataSectionBlur}
        onItemDelete={onItemDelete}
        autoFocus={autoFocus}
      />
    </Flex>
  );
};
